import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, Pencil, Trash2, Eye, EyeOff, Calendar, 
  MoreVertical, FileText, Search 
} from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

const CATEGORY_COLORS = {
  'Comprar': 'bg-emerald-500/20 text-emerald-400',
  'Vender': 'bg-amber-500/20 text-amber-400',
  'Rentar': 'bg-blue-500/20 text-blue-400',
  'Mercado Guatemala': 'bg-purple-500/20 text-purple-400'
};

const BlogAdminPage = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await apiClient.get('/blog/admin/articles');
      setArticles(response.data.articles || []);
    } catch (error) {
      toast.error('Error al cargar los artículos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await apiClient.delete(`/blog/articles/${deleteId}`);
      setArticles(articles.filter(a => a.id !== deleteId));
      toast.success('Artículo eliminado');
    } catch (error) {
      toast.error('Error al eliminar el artículo');
    } finally {
      setDeleteId(null);
    }
  };

  const togglePublish = async (article) => {
    try {
      await apiClient.put(`/blog/articles/${article.id}`, {
        is_published: !article.is_published
      });
      
      setArticles(articles.map(a => 
        a.id === article.id 
          ? { ...a, is_published: !a.is_published }
          : a
      ));
      
      toast.success(article.is_published ? 'Artículo despublicado' : 'Artículo publicado');
    } catch (error) {
      toast.error('Error al actualizar el artículo');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-GT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: articles.length,
    published: articles.filter(a => a.is_published).length,
    drafts: articles.filter(a => !a.is_published).length,
    totalViews: articles.reduce((sum, a) => sum + (a.views || 0), 0)
  };

  return (
    <AdminLayout>
      <div className="p-6" data-testid="blog-admin-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl text-white font-medium">Blog</h1>
            <p className="text-[#A3A3A3] text-sm mt-1">
              Gestiona los artículos del blog
            </p>
          </div>
          <Link to="/admin/blog/nuevo">
            <Button className="bg-[#D4AF37] text-black hover:bg-[#FFE082]" data-testid="new-article-btn">
              <Plus size={16} className="mr-2" />
              Nuevo Artículo
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#121212] border border-white/5 rounded-xl p-4">
            <p className="text-[#A3A3A3] text-xs uppercase mb-1">Total</p>
            <p className="text-2xl text-white font-medium">{stats.total}</p>
          </div>
          <div className="bg-[#121212] border border-white/5 rounded-xl p-4">
            <p className="text-[#A3A3A3] text-xs uppercase mb-1">Publicados</p>
            <p className="text-2xl text-emerald-400 font-medium">{stats.published}</p>
          </div>
          <div className="bg-[#121212] border border-white/5 rounded-xl p-4">
            <p className="text-[#A3A3A3] text-xs uppercase mb-1">Borradores</p>
            <p className="text-2xl text-amber-400 font-medium">{stats.drafts}</p>
          </div>
          <div className="bg-[#121212] border border-white/5 rounded-xl p-4">
            <p className="text-[#A3A3A3] text-xs uppercase mb-1">Vistas Totales</p>
            <p className="text-2xl text-blue-400 font-medium">{stats.totalViews}</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" size={18} />
          <Input
            placeholder="Buscar artículos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10 text-white"
          />
        </div>

        {/* Articles List */}
        {loading ? (
          <div className="text-center py-20 text-[#A3A3A3]">Cargando artículos...</div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-12 h-12 text-[#333] mx-auto mb-4" />
            <p className="text-[#A3A3A3]">
              {searchTerm ? 'No se encontraron artículos' : 'No hay artículos aún'}
            </p>
            {!searchTerm && (
              <Link to="/admin/blog/nuevo">
                <Button className="mt-4 bg-[#D4AF37] text-black hover:bg-[#FFE082]">
                  Crear primer artículo
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="bg-[#121212] border border-white/5 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-[#A3A3A3] text-xs uppercase p-4">Artículo</th>
                  <th className="text-left text-[#A3A3A3] text-xs uppercase p-4 hidden md:table-cell">Categoría</th>
                  <th className="text-left text-[#A3A3A3] text-xs uppercase p-4 hidden lg:table-cell">Estado</th>
                  <th className="text-left text-[#A3A3A3] text-xs uppercase p-4 hidden lg:table-cell">Vistas</th>
                  <th className="text-left text-[#A3A3A3] text-xs uppercase p-4 hidden md:table-cell">Fecha</th>
                  <th className="text-right text-[#A3A3A3] text-xs uppercase p-4">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredArticles.map((article) => (
                  <tr 
                    key={article.id} 
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`blog-row-${article.slug}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#1a1a1a] flex-shrink-0">
                          {article.cover_image ? (
                            <img 
                              src={article.cover_image} 
                              alt="" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#333]">
                              <FileText size={20} />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate max-w-[200px] lg:max-w-[300px]">
                            {article.title}
                          </p>
                          <p className="text-[#666] text-xs truncate max-w-[200px]">
                            /blog/{article.slug}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <span className={`px-2 py-1 text-xs rounded-full ${CATEGORY_COLORS[article.category]}`}>
                        {article.category}
                      </span>
                    </td>
                    <td className="p-4 hidden lg:table-cell">
                      {article.is_published ? (
                        <Badge className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30">
                          <Eye size={12} className="mr-1" />
                          Publicado
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-amber-500/30 text-amber-400">
                          <EyeOff size={12} className="mr-1" />
                          Borrador
                        </Badge>
                      )}
                    </td>
                    <td className="p-4 hidden lg:table-cell">
                      <span className="text-[#A3A3A3]">{article.views || 0}</span>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <span className="text-[#A3A3A3] text-sm">
                        {formatDate(article.published_at || article.created_at)}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#1a1a1a] border-white/10">
                          <DropdownMenuItem 
                            onClick={() => navigate(`/admin/blog/${article.id}`)}
                            className="text-white hover:bg-white/10 cursor-pointer"
                          >
                            <Pencil size={14} className="mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => window.open(`/blog/${article.slug}`, '_blank')}
                            className="text-white hover:bg-white/10 cursor-pointer"
                          >
                            <Eye size={14} className="mr-2" />
                            Ver
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => togglePublish(article)}
                            className="text-white hover:bg-white/10 cursor-pointer"
                          >
                            {article.is_published ? (
                              <>
                                <EyeOff size={14} className="mr-2" />
                                Despublicar
                              </>
                            ) : (
                              <>
                                <Eye size={14} className="mr-2" />
                                Publicar
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => setDeleteId(article.id)}
                            className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                          >
                            <Trash2 size={14} className="mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent className="bg-[#1a1a1a] border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">¿Eliminar artículo?</AlertDialogTitle>
              <AlertDialogDescription className="text-[#A3A3A3]">
                Esta acción no se puede deshacer. El artículo será eliminado permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/10">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-500 text-white hover:bg-red-600"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default BlogAdminPage;
